/* eslint-disable arrow-body-style */
/**
 * 性能优化说明：
 * 
 * 主要优化点：
 * 1. 预计算legend维度的唯一值 - 避免在forEach循环中重复计算
 * 2. 预计算维度值分布 - 避免在getLegendName函数中重复计算
 * 3. 使用Map缓存计算结果 - 减少重复的数组遍历和过滤操作
 * 4. 一次性数据收集 - 减少对data数组的重复遍历
 * 
 * 性能提升：
 * - 时间复杂度：从 O(n × m × k) 优化到 O(n × m)
 * - 空间复杂度：增加缓存空间，但显著减少计算时间
 * - 特别适用于大数据量场景，避免浏览器崩溃
 */
import { getMetricDisplayName } from "@/pages/aigc/utils/dataTransform";
import { DATE_FORMAT_MAP, DIMENSION_NAME_MAP, DIMENSION_TAB_NAME_MAP, SOURCE_DIMENSION_NAME_MAP } from "../../../config/constant";
import { uniqBy, toLower, toString } from "lodash-es";
import { DimensionItem, DimensionValueItem, IColumn, IFilterItem, TabOption, TabRenderConfig, TChartData } from "./_type";
import dateTimeFormat from "@home/utils/dbDataFormat/dateTimeFormat";
import { IFilterInfo } from "./hooks";
import {
  buildDimensionColumns,
  buildFirstDimensionColumn,
  buildMetricColumns,
  resolveDateFormatter,
  buildAdditionalColumns,
} from "../Download/utils/tableHelpers";


/**
 * 从 data_key 中提取字符串值
 * 支持两种格式：字符串或对象 { data_key: string, yAxis_index?: number }
 */
export const extractMetricDataKey = (dataKey?: string | { data_key: string; yAxis_index?: number; }): string => {
  if (!dataKey) {
    return '';
  }
  if (typeof dataKey === 'string') {
    return dataKey;
  }
  return dataKey?.data_key || '';
};

/**
 * 从 data_key 中提取 yAxis_index 值
 * 支持两种格式：字符串或对象 { data_key: string, yAxis_index?: number }
 */
export const extractYAxisIndex = (dataKey?: string | { data_key: string; yAxis_index?: number; }): number => {
  if (dataKey && typeof dataKey === 'object' && dataKey !== null) {
    return typeof dataKey?.yAxis_index === 'number' ? dataKey?.yAxis_index : 0;
  }
  return 0;
};
/**
 * 根据输入值生成选项卡选项数组
 * @param values 输入值数组，可以是字符串或DimensionValueItem对象
 * @param key 维度键名，用于获取维度标签
 * @returns 返回格式化后的TabOption数组
 */
export const generateOptions = (values: (string | DimensionValueItem)[], key: string): TabOption[] => {
  return values?.map(item => {
    if (typeof item === 'object' && item !== null) {
      const displayLabel = getDissionLable(key, item.value, item?.name );
      return {
        label: displayLabel || item.value,
        value: item.value,
        icon: item.icon,
        icon_active: item.icon_active,
        defaultValue: values?.[0]?.value || values?.[0] || ''
      };
    }
    const displayLabel = getDissionLable(key, item);
    return { label: displayLabel || item, value: item };
  });
};

/**
 * 构建级联子节点
 */
const buildCascadeChildren = (
  currentLevelIndex: number,
  data: any,
  levels: string[],
  dimensionInfo: DimensionItem[]
): TabOption[] => {
  const currentLevelKey = levels[currentLevelIndex];

  if (!data || !currentLevelKey) return [];

  // metrics 特殊处理
  if (currentLevelKey === 'metrics') {
    if (Array.isArray(data)) {
      const options = data.map(metricKey => ({
        label: getDissionLable('metrics', metricKey),
        value: metricKey,
        currentTabListKey: 'metrics',

        isMetric: true,
      }));
      return [{
        options,
        currentTabListKey: 'metrics',
        name: 'Metrics',
        defaultValue: options?.[0]?.value,
      },
      ];
    }
    return [];
  }

  // 查找当前层级的维度信息
  const baseDimension = dimensionInfo?.find(dim => dim.data_key === currentLevelKey);

  // 将原始 key 转换为 DimensionValueItem 格式，并尽量使用 dimensionInfo 补充信息
  const keys = Object.keys(data);
  const rawValues = keys.map(key => {
    const item = data[key];
    let result: DimensionValueItem & { value: string; } = { value: key };

    if (baseDimension) {
      const foundInDimension = baseDimension.value.find(
        v => v !== null && (typeof v === 'object' ? v.value === key : v === key)
      );
      if (foundInDimension && typeof foundInDimension === 'object') {
        result = { ...result, ...foundInDimension, value: key };
      }
    }

    if (typeof item === 'object' && item !== null) {
      result = { ...result, ...item };
    }

    return result;
  });

  // 使用 generateOptions 统一生成 label/value/icon
  const baseOptions = generateOptions(rawValues, currentLevelKey);

  // 再递归构建子级
  return baseOptions.map(option => {
    const valueItem = option.value;
    const childData = data[valueItem];
    const children = buildCascadeChildren(currentLevelIndex + 1, childData, levels, dimensionInfo);
    const childTabListKey = children?.[0]?.currentTabListKey;

    return {
      ...option,
      currentTabListKey: currentLevelKey,
      name: getDimensionName(currentLevelKey, dimensionInfo),
      ...(childTabListKey ? { childTabListKey } : {}),
      ...(children.length > 0 ? { options: children } : {}),
    };
  });
};

/**
 * 生成 tab 渲染数据
 * @param filterInfo 筛选器信息
 * @param dimensionInfo 维度信息
 * @returns Record<tabKey, TabRenderConfig>
 */
export const generateTabRenderData = (
  filterInfo: IFilterItem[] = [],
  dimensionInfo: DimensionItem[] = [],
  // metricObj: {
  //   data_key: string;
  //   icon?: string;
  //   icon_active?: string;
  //   name?: string;
  //   type?: string;
  // },
): Record<string, TabRenderConfig> => {
  const result: Record<string, TabRenderConfig> = {};

  filterInfo.forEach(filter => {
    const { data_key, filter_type, display_info, display_value, value } = filter;

    if (filter_type === 'normal') {
      const dimension = dimensionInfo.find(dim => dim.data_key === data_key);
      if (dimension) {
        const options = generateOptions(dimension.value, data_key);
        result[data_key] = {
          type: 'normal',
          options,
          currentTabListKey: data_key,
          name: getDimensionName(data_key, dimensionInfo),
          defaultValue: options?.[0]?.value,
        };
      }
    } else if (filter_type === 'cascade') {
      const levels = [...(display_info?.slice(1) || []), 'metrics'];

      if (value && display_value) {
        // 从 dimensionInfo 获取完整维度数据
        const dimension = dimensionInfo.find(dim => dim.data_key === data_key);
        const fullValues = value.map(valueItem => {
          if (dimension) {
            const found = dimension.value.find(
              v => v !== null && (typeof v === 'object' ? v.value === valueItem : v === valueItem)
            );
            if (found) {
              return typeof found === 'string' ? { value: found } : found;
            }
          }
          return { value: valueItem };
        });

        // 使用完整维度数据生成基础选项
        const baseOptions = generateOptions(fullValues, data_key);

        // 构建子级结构
        const options = baseOptions.map(option => {
          const valueItem = option.value;
          const children = buildCascadeChildren(0, display_value?.[valueItem], levels, dimensionInfo);
          const childTabListKey = children?.[0]?.currentTabListKey;
          return {
            ...option,
            currentTabListKey: data_key,
            name: getDimensionName(data_key, dimensionInfo),
            ...(childTabListKey ? { childTabListKey } : {}),
            ...(children.length > 0 ? { options: children } : {}),
          };
        });
        result[data_key] = {
          type: 'cascade',
          options,
          currentTabListKey: data_key,
          name: getDimensionName(data_key, dimensionInfo),
          childTabListKey: levels[0],
          defaultValue: value?.[0],
        };
      }
    }
  });
  return result;
};

export const getDimensionName = (key: string, dimensionInfo: DimensionItem[]) => {
  return dimensionInfo?.find(dim => dim.data_key === key)?.name || DIMENSION_TAB_NAME_MAP?.[key] || key;
};

/**
 * 获取所有 currentTabListKey 对应的 defaultValue
 */
export const getDefaultValuesMap = (data: Record<string, TabRenderConfig>, excludeKey = 'metrics') => {
  const result: Record<string, string> = {};

  const traverse = (node: any) => {
    if (!node) return;

    // 如果当前节点有 default 值
    if (typeof node === 'object' && node.currentTabListKey && 'defaultValue' in node) {
      result[node.currentTabListKey] = node.defaultValue;
    }

    // 遍历子节点
    if (Array.isArray(node.options)) {
      node.options.forEach(traverse);
    }

    // 遍历数组（如 options）
    if (Array.isArray(node)) {
      node.forEach(traverse);
    }
  };

  Object.values(data).forEach(traverse);
  const { [excludeKey]: excludeDefault, ...others } = result || {};
  return {
    defaultValue: others,
    [excludeKey]: excludeDefault
  };
};
/**
 * 获取维度对应的显示名称
 */
export const getDissionLable = (key: string, value: string, name?: string): string => {
  try {
    const v = toString(value);
    const lv = toLower(v);
    const result = name || DIMENSION_NAME_MAP?.[key]?.[v] || DIMENSION_NAME_MAP?.[key]?.[lv];
    if (key === 'market_name') {
      return result || DIMENSION_NAME_MAP?.region_name?.[v] || DIMENSION_NAME_MAP?.region_name?.[lv] || v;
    }
    if (key === 'source') {
      return SOURCE_DIMENSION_NAME_MAP?.[v] || SOURCE_DIMENSION_NAME_MAP?.[lv] || result || v;
    }
    return result || v;
  } catch {
    return toString(value) || '';
  }
};

// 过滤数据，根据filterInfoVal过滤数据
export const filterNewData = (
  dataList: any[], 
  filterInfoVal: Record<string, any>,
  dimensionInfo?: DimensionItem[]
): any[] => {
  if (!filterInfoVal) return dataList;

  const { metrics, ...rest } = filterInfoVal;
  
  // 先过滤数据，再预处理（性能优化：避免对不需要的数据进行预处理）
  const filteredData = dataList?.filter((item) => {
    const baseMatch = Object.entries(rest).every(([key, value]) => {
      return item[key] === value;
    });
    return baseMatch;
  }) || [];
  
  // 对过滤后的数据进行预处理：添加格式化后的维度显示名称
  return filteredData?.map(item => {
    const processedItem = { ...item };
    
    // 预处理所有维度字段，包括有映射和无映射的维度
    if (dimensionInfo && Array.isArray(dimensionInfo)) {
      dimensionInfo.forEach(dimension => {
        const dimensionKey = dimension.data_key;
        const originalValue = item?.[dimensionKey];
        
        if (originalValue !== undefined && originalValue !== null) {
          // 获取维度名称
          const dimensionName = getDimensionValueName(dimensionInfo, dimensionKey, originalValue);
          
          // 对于有映射的维度，使用 getDissionLable 格式化
          // 对于无映射的维度，直接使用 dimensionName 或原始值
          const hasMapping = DIMENSION_NAME_MAP[dimensionKey] || SOURCE_DIMENSION_NAME_MAP[dimensionKey];
          const displayName = hasMapping 
            ? (getDissionLable(dimensionKey, originalValue, dimensionName) || originalValue)
            : (dimensionName || originalValue);
          
          // 添加预处理后的显示名称
          processedItem[`${dimensionKey}_display`] = displayName;
        }
      });
    }
    
    return processedItem;
  }) || [];
};

/**
 * 将 tableInfo.data 中的 date 与 time 字段按粒度进行格式化
 * @param tableInfo 表格信息对象，包含 data 数组
 * @param granularity 粒度（如 'day' | 'week' | 'month' | 'realtime' 等），用于选择格式
 * @returns 返回一个新的 tableInfo，data 中的 date/time 已格式化
 */
export const formatTableInfoDateTime = (
  tableInfo: { data: any[]; [key: string]: any; },
  granularity?: string
) => {
  const dateFormatter = DATE_FORMAT_MAP?.[granularity as keyof typeof DATE_FORMAT_MAP] || 'day';
  const formattedData = (tableInfo?.data || []).map((item) => ({
    ...item,
    date: item?.date ? dateTimeFormat(item.date, dateFormatter) : item?.date,
    time: item?.time ? dateTimeFormat(item.time, dateFormatter) : item?.time,
  }));

  return {
    ...tableInfo,
    data: formattedData,
  };
};

/**
 * 获取图例名称
 * @param param0
 * @returns
 */
const getLegendName = ({
  item,
  legends,
  legendShowNameKey,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  data,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  dimensionValuesMap,
  dimensionUniqueValuesMap,
  // isHaveGameName,
  metricName,
}: {
  legendShowNameKey: string;
  item: any;
  legends: string[];
  dimensionUniqueValuesMap?: Map<string, any[]>;
  // isHaveGameName: boolean;
  metricName?: string;
  dimensionInfo?: DimensionItem[];
}): string => {

  if (legends?.length > 0 && legendShowNameKey) {
    // ===== 修复：使用getDimensionValueName获取正确的name值 =====
    // const showNameDimensionName = getDimensionValueName(dimensionInfo || [], legendShowNameKey, item?.[legendShowNameKey]);
    // 在 getLegendName 中
    const showName = item?.[`${legendShowNameKey}_display`] || item?.[legendShowNameKey] || '';

    // ===== 性能优化：使用预计算的值映射 =====
    // 原始逻辑：每次调用都重新计算每个维度的唯一值
    // 优化后：使用预计算的dimensionUniqueValuesMap，避免重复计算
    const allDimensionsHaveSameValue = legends
      .filter(legend => legend !== legendShowNameKey)
      .every(legend => {
        const uniqueValues = dimensionUniqueValuesMap?.get(legend) || [];
        return (uniqueValues.length === 1 && uniqueValues[0] === item[legend]) || uniqueValues.length === 0;
      });

    if (allDimensionsHaveSameValue) {
      return showName;
    }
    
    // ===== 修改：按 legends 顺序拼接，legendShowNameKey 如果在第一个位置则放在最前面 =====
    // 按 legends 数组的顺序拼接，legendShowNameKey 会在对应位置
    const legendNameParts = legends.map(legend => {
      if (legend === legendShowNameKey) {
        return showName;
      }
      const legendKey = legend;
      // 使用预处理的结果，避免重复调用 getDissionLable
      return item?.[`${legendKey}_display`] || item?.[legendKey] || '';
    }).filter(Boolean);
   
    return legendNameParts.join('_');
  }
  // 无legend，则展示指标
  return metricName || '';
};

// 过滤空的legend数据
const processChartData = (chartData: TChartData) => {
  // 获取所有数据点y都为空字符串的name数组
  const emptyLegends = chartData
    .filter(item => item.data.every(d => d.y === ""))
    .map(item => item.name);

  // 移除所有数据点y都为空字符串的对象
  const filteredChartData = chartData.filter(item =>
    !item.data.every(d => d.y === "")
  );

  return {
    emptyLegends,
    filteredChartData
  };
};

/**
 * 将dimensionInfo数组按照是否包含在legends中分为两个数组
 * @param dimensionInfo 维度信息数组
 * @param legends 需要匹配的维度键名数组
 * @returns 包含两个数组的对象：{ legendDimensions, otherDimensions }
 */
export const splitDimensionsByLegends = (
  dimensionInfo: DimensionItem[],
  legends: string[]
): { legendDimensions: DimensionItem[]; otherDimensions: DimensionItem[]; } => {
  const legendDimensions: DimensionItem[] = [];
  const otherDimensions: DimensionItem[] = [];

  dimensionInfo.forEach(item => {
    if (legends.includes(item?.data_key)) {
      legendDimensions.push(item);
    } else {
      otherDimensions.push(item);
    }
  });

  return { legendDimensions, otherDimensions };
};

// 提取为通用函数，用于生成列配置
export const generateColumns = (
  dimensions: DimensionItem[],
  xAxis: any[] | undefined,
  dateFormatter: string,
  fullDimensionInfo?: DimensionItem[] // 可选的完整 dimensionInfo，用于 getDimensionName
): IColumn[] => {
  return dimensions
    ?.filter(item => item?.data_key !== (xAxis?.[0]?.date_key || xAxis?.[0]))
    ?.map(item => {
      const key = item?.data_key;
      // 如果提供了完整的 dimensionInfo，使用 getDimensionName；否则使用 item?.name
      const dimensionName = fullDimensionInfo
        ? getDimensionName(key, fullDimensionInfo)
        : (item?.name || DIMENSION_TAB_NAME_MAP?.[key] || key);
      return {
        name: dimensionName,
        name_en: dimensionName,
        origin_name: key,
        display_name: `${key}_display`,
        isDimension: true,
        can_sort: false,
        type: 0,
        column_date_type: key === 'date' ? (dateFormatter || 'day') : 'string',
        accuracy: null,
        table_code: '',
      };
    }) || [];
};

export const getChartComData = ({
  inputData,
  filterInfoVal,
  xAxis,
  // yAxis = ['value'],
  inputMetricsInfo,
  inputLegends: legends,
  // chartType = 'trend',
  dimensionInfo,
  metric,
  metricName,
  additional_table_field,
}) => {
  const dateFormatter = resolveDateFormatter(filterInfoVal?.granularity);
  const data = filterNewData(inputData, filterInfoVal, dimensionInfo);
  // 修复：使用 extractMetricDataKey 提取字符串进行比较，支持对象格式的 data_key
  const metricsInfo = inputMetricsInfo?.filter(item => {
    const itemDataKey = extractMetricDataKey(item?.data_key);
    return itemDataKey === metric;
  });
  const hideEmptyLegends = metricsInfo?.[0]?.hide_empty_legends !== false;
  // Step 1: 根据 filterInfoVal 过滤数据
  const filteredData = data.filter(item => {
    return Object.entries(filterInfoVal).every(([key, value]) => {
      // if (key === 'metrics') return true;
      if (value === null || value === undefined) return true;
      // debugger
      const itemValue = item[key];
      return typeof itemValue === 'string' && typeof value === 'string'
        ? itemValue.toLowerCase() === value.toLowerCase()
        : itemValue === value;
    });
  });
  // Step 2: 获取指标键
  if (!metric) return {
    chartData: [], tableInfo: {
      data: [],
      columns: [],
    }
  };

  // Step 3: 分组图例数据 + 收集所有 x 值
  const legendGroups: Record<string, any[]> = {};
  const xAxisCategoriesSet = new Set();

  // ===== 性能优化：预计算legend维度的唯一值 =====
  // 原始逻辑：在forEach循环中每次迭代都重新计算legendShowNameKeyArr
  // 优化后：提前计算并缓存结果，避免重复计算
  const legendUniqueValuesMap = new Map();
  const legendShowNameKeyArr = legends.filter(legend => {
    if (!legendUniqueValuesMap.has(legend)) {
      const allValues = filteredData.map(item => item[legend]);
      const filteredValues = allValues.filter(v => v !== undefined && v !== null);
      const uniqueValues = [...new Set(filteredValues)];
      legendUniqueValuesMap.set(legend, uniqueValues);
      return uniqueValues.length > 1;
    } 
    const uniqueValues = legendUniqueValuesMap.get(legend);
    return uniqueValues.length > 1;
    
  });

  // ===== 修复：添加fallback逻辑，如果没有找到多值的legend，使用第一个legend =====
  // 如果没有找到多值的legend，使用第一个legend
  const finalLegendShowNameKeyArr = legendShowNameKeyArr.length > 0 ? legendShowNameKeyArr : [legends?.[0]].filter(Boolean);

  const legendShowNameKey = finalLegendShowNameKeyArr?.[0];
  // 是否是单维度生成的图例
  // const isSingleDimensionLegend = finalLegendShowNameKeyArr?.length === 1;
  // ===== 性能优化：预计算维度值分布 =====
  // 原始逻辑：在getLegendName函数中重复计算每个维度的唯一值
  // 优化后：一次性遍历收集所有维度值，避免重复计算
  const dimensionValuesMap = new Map();
  const dimensionUniqueValuesMap = new Map();

  // 一次性遍历data，收集所有维度的值
  const dimensionValuesCollector = new Map();
  data.forEach(item => {
    legends.forEach(legend => {
      if (!dimensionValuesCollector.has(legend)) {
        dimensionValuesCollector.set(legend, []);
      }
      const value = item[legend];
      if (value !== undefined && value !== null) {
        dimensionValuesCollector.get(legend).push(value);
      }
    });
  });
  
  // 处理收集到的值
  legends.forEach(legend => {
    const values = dimensionValuesCollector.get(legend) || [];
    dimensionValuesMap.set(legend, values);
    dimensionUniqueValuesMap.set(legend, [...new Set(values)]);
  });

  // ===== 性能优化：使用预计算的结果 =====
  // 原始逻辑：每次迭代都重新计算legendShowNameKeyArr和维度值
  // 优化后：使用预计算的结果，显著减少计算量
  filteredData.forEach(item => {
    const x = item?.[xAxis?.[0]?.date_key || xAxis?.[0]];
    const y = item?.[metric];
    
    // 第一个legendShowNameKey设定为最后展示的话术内容，传入legendShowNameKeyArr，图例只展示值不唯一的维度内容
    const legendName = getLegendName({
      item,
      legends: finalLegendShowNameKeyArr,
      legendShowNameKey,
      dimensionUniqueValuesMap, // 传入预计算的唯一值映射
      // isHaveGameName,
      metricName,
    });

    if (!legendGroups[legendName]) {
      legendGroups[legendName] = [];
    }
    legendGroups[legendName].push({ x, y });
    xAxisCategoriesSet.add(x);
  });
  // Step 4: 补全每个分组中的 x 值，缺失的用 null 填充
  const xAxisCategories = [...xAxisCategoriesSet];

  const completeLegendGroups = {};
  Object.entries(legendGroups).forEach(([legendName, groupData]) => {
    const map = {};
    groupData.forEach(({ x, y }) => {
      map[x] = y;
    });

    completeLegendGroups[legendName] = xAxisCategories.map(x => ({
      x,
      y: map[x] !== undefined ? map[x] : ''
    }));
  });

  // Step 5: 构建 chartData
  let chartData: TChartData = Object.entries(completeLegendGroups).map(([name, data]) => ({
    name,
    stack: 'Total',
    // type: chartType === 'bar' ? 'bar' : 'line',
    data: data.map(({ x, y }) => ({ x, y }))
  }));
  // let emptyLegendsName = [];
  if(hideEmptyLegends) {
    const chartFilterData = processChartData(chartData);
    chartData = chartFilterData?.filteredChartData;
    // emptyLegendsName = chartFilterData?.emptyLegends;
  }
  // ===== 计算 legend 顺序，并用它重排 series（chartData） =====
  // let legendOrder: string[] = [];
  // if (isSingleDimensionLegend) {
  //   legendOrder = getLegendOrder(chartData, legendShowNameKey, dimensionInfo);
  // } else {
  //   legendOrder = getMultiDimensionLegendOrder(chartData, finalLegendShowNameKeyArr, dimensionInfo);
  // }
  // 确保 legend/series/颜色/tooltip 顺序完全一致。使用legendOrder重排chartData
  // 如果直接传递legendOrder给后面的图表作为legend的打标签内容，担心可能会导致标签内容不一致。
  chartData = reorderChartDataByDimensions(chartData, finalLegendShowNameKeyArr, dimensionInfo);
  let allColumns: IColumn [] = [];
  const firstColumns = buildFirstDimensionColumn(xAxis, dimensionInfo as any, filterInfoVal?.granularity, 'chart');
  
  const metricColumns: IColumn[] = buildMetricColumns(
    metricsInfo as any,
    metric,
    (metricDataKey: string) => getMetricDisplayName(metricDataKey || ''),
    'chart'
  );

  const { legendDimensions: legendsColumnsArr, otherDimensions: dimensionColumnsArr } = splitDimensionsByLegends(dimensionInfo || [], legends || []);
  const legendsColumns = buildDimensionColumns(legendsColumnsArr as any, xAxis, dateFormatter, dimensionInfo as any, 'chart');
  const dimensionColumns = buildDimensionColumns(dimensionColumnsArr as any, xAxis, dateFormatter, dimensionInfo as any, 'chart');

  // 生成 additional_table_field 的列配置（复用公共方法）
  const additionalColumns: IColumn[] = buildAdditionalColumns(additional_table_field, 'chart');

  allColumns = [firstColumns, ...(legendsColumns || []), ...(metricColumns || []), ...(dimensionColumns || []),...(additionalColumns || [])];
  
  const tableInfo = {
    data,
    columnsInfo: uniqBy(allColumns, 'origin_name')
  };
  return { chartData, 
    tableInfo: formatTableInfoDateTime(tableInfo, filterInfoVal?.granularity),
    // 新增：单维度图例顺序配置
    // legendOrder: (isSingleDimensionLegend
    //   ?  getLegendOrder(chartData, legendShowNameKey, dimensionInfo)
    //   : sortMultiDimensionLegends(chartData, finalLegendShowNameKeyArr, dimensionInfo))
  };
};


/**
 * 更新级联筛选器值的函数
 * @param oldFilterInfoVal 旧的状态值对象
 * @param filterInfo 筛选器配置数组
 * @param data 新的修改数据
 * @returns 更新后的状态值对象
 */

/**
 * 级联筛选器更新函数
 * @param oldFilterInfoVal 旧的筛选器值对象
 * @param filterInfo 筛选器配置数组
 * @param data 新的更新数据
 * @returns 更新后的筛选器值对象
 */
export const updateFilterInfoVal = (
  oldFilterInfoVal: Record<string, string> | null,
  filterInfo: IFilterInfo,
  data: Record<string, string>
): Record<string, string> => {
  // 创建新对象，避免直接修改原状态
  const newFilterInfoVal = { ...(oldFilterInfoVal || {}) };

  // 1. 首先处理所有普通(normal)类型的筛选器
  filterInfo.forEach((item) => {
    // 检查是否是普通筛选器且有对应的新数据
    if (item.filter_type === "normal" && data[item.data_key] !== undefined) {
      // 直接更新普通筛选器的值
      newFilterInfoVal[item.data_key] = data[item.data_key];
    }
  });

  // 2. 处理级联(cascade)类型的筛选器
  filterInfo.forEach((item) => {
    // 只处理级联类型的筛选器
    if (item.filter_type === "cascade") {
      // 解构获取级联筛选器的配置项
      const { data_key, display_info = [], display_value = {} } = item;

      // 情况1: 如果修改了级联主字段(如game_type)
      if (data[data_key] !== undefined) {
        // 更新主字段的值
        newFilterInfoVal[data_key] = data[data_key];
        
        // 更新所有依赖此主字段的字段，尝试保留有效旧值
        updateDependentFields(
          data_key,                   // 当前修改的字段key
          data[data_key],             // 当前字段的新值
          display_info,               // 所有依赖字段信息
          display_value,              // 级联值结构
          newFilterInfoVal,           // 需要更新的结果对象
          oldFilterInfoVal || {}      // 旧值对象，用于检查保留值
        );
      }

      // 情况2: 检查是否修改了依赖字段(如platform或country_name)
      display_info.forEach((depField, index) => {
        // 如果此字段被修改且不是主字段
        if (data[depField] !== undefined && depField !== data_key) {
          // 构建此字段的完整访问路径(如[game_type值, platform值])
          const path = buildFieldPath(depField, display_info, newFilterInfoVal);
          
          // 验证新值在当前位置是否有效
          if (isValidValue(path, data[depField], display_value)) {
            // 更新字段值
            newFilterInfoVal[depField] = data[depField];
            
            // 更新此字段的所有下级字段，尝试保留有效旧值
            updateDependentFields(
              depField,                      // 当前修改的字段
              data[depField],                // 新值
              display_info.slice(index + 1), // 只处理后面的字段
              getNestedValue(display_value as string, path as any), // 获取当前层级的选项
              newFilterInfoVal,              // 结果对象
              oldFilterInfoVal || {}         // 旧值对象
            );
          }
        }
      });
    }
  });

  return newFilterInfoVal;
};

/**
 * 更新依赖字段，优先保留有效的旧值
 * @param currentKey 当前修改的字段key
 * @param currentValue 当前字段的新值
 * @param dependentFields 需要更新的依赖字段数组
 * @param optionsData 级联选项数据
 * @param result 需要更新的结果对象
 * @param oldValues 旧值对象，用于检查保留值
 */
function updateDependentFields(
  currentKey: string,
  currentValue: string,
  dependentFields: string[],
  optionsData: Record<string, any>,
  result: Record<string, string>,
  oldValues: Record<string, string>
): void {
  // 获取当前值对应的选项(如pc对应的{ios:{...}, android:{...}})
  let currentOptions = optionsData?.[currentValue];
  
  // 遍历所有需要更新的依赖字段
  dependentFields.forEach((depField) => {
    // 不处理当前字段本身
    if (depField !== currentKey) {
      // 检查旧值在新选项中是否仍然有效
      const oldValue = oldValues[depField];
      if (oldValue && currentOptions?.[oldValue] !== undefined) {
        // 保留有效的旧值
        result[depField] = oldValue;
        // 继续检查下一级选项
        currentOptions = currentOptions[oldValue];
      } else {
        // 如果没有有效旧值，则取第一个有效选项
        if (currentOptions && typeof currentOptions === "object") {
          const firstValidKey = Object.keys(currentOptions)[0];
          if (firstValidKey) {
            // 设置第一个有效值
            result[depField] = firstValidKey;
            // 继续检查下一级选项
            currentOptions = currentOptions[firstValidKey];
            return;
          }
        }
        // 没有有效选项则删除该字段
        delete result[depField];
      }
    }
  });
}

/**
 * 构建字段访问路径
 * @param targetField 目标字段
 * @param displayInfo 所有字段显示信息
 * @param currentValues 当前字段值集合
 * @returns 访问路径数组
 */
function buildFieldPath(
  targetField: string,
  displayInfo: string[],
  currentValues: Record<string, string>
): string[] {
  const path: string[] = [];
  let found = false;
  
  // 遍历所有字段，构建到目标字段的路径
  for (const field of displayInfo) {
    // 找到目标字段时停止
    if (field === targetField) {
      found = true;
      break;
    }
    // 只添加有值的字段到路径
    if (currentValues[field]) {
      path.push(currentValues[field]);
    }
  }
  
  // 返回构建好的路径(如['pc','ios'])
  return found ? path : [];
}

/**
 * 验证值是否有效
 * @param path 访问路径
 * @param value 要验证的值
 * @param displayValue 级联值结构
 * @returns 是否有效
 */
function isValidValue(
  path: string[],
  value: string,
  displayValue: Record<string, any>
): boolean {
  let current = displayValue;
  // 沿着路径深入检查
  for (const key of path) {
    current = current?.[key];
    if (!current) return false; // 路径中断则无效
  }
  // 检查值是否存在
  return value in current;
}

/**
 * 获取嵌套值
 * @param obj 根对象
 * @param path 访问路径
 * @returns 嵌套值或undefined
 */
function getNestedValue(
  obj: Record<string, any>,
  path: string[]
): Record<string, any> | undefined {
  let current = obj;
  // 沿着路径深入获取值
  for (const key of path) {
    current = current?.[key];
    if (!current) return undefined; // 路径不存在则返回undefined
  }
  return current;
}

// 将curOptions中的顺序按照allOptions中元素value的顺序重新排序
export const sortCurOptionsByAllOptions = (
  allOptions: Array<string | { value: string; }>,
  curOptions: string[]
): string[] => {
  // 1. 统一转换为 value 字符串数组
  const normalizedAllOptions = Array.isArray(allOptions)
    ? allOptions.map(option => 
      typeof option === 'string' ? option : option.value
    )
    : [];

  // 2. 建立 value → index 的映射
  const orderMap = new Map<string, number>();
  normalizedAllOptions.forEach((value, index) => {
    orderMap.set(value, index);
  });

  // 3. 排序当前选项
  return [...curOptions].sort((a, b) => {
    const indexA = orderMap.get(a);
    const indexB = orderMap.get(b);

    // 处理不存在的值（按原始顺序或置后）
    if (indexA === undefined) return 1;
    if (indexB === undefined) return -1;

    return indexA - indexB;
  });
};

/**
 * 从dimensionInfo中获取指定维度和值的name字段
 * @param dimensionInfo 维度信息数组
 * @param dimensionKey 维度键名
 * @param value 要查找的值
 * @returns 对应的name值，如果找不到则返回undefined
 */
export const getDimensionValueName = (
  dimensionInfo: DimensionItem[],
  dimensionKey: string,
  value: string
): string | undefined => {
  const dimensionItem = dimensionInfo?.find(dim => dim.data_key === dimensionKey);

  if (!dimensionItem || !Array.isArray(dimensionItem.value)) {
    return undefined;
  }
  
  // 查找value对应的对象，获取name字段
  const valueItem = dimensionItem.value.find((v: any) => {
    if (typeof v === 'object' && v !== null) {
      return v.value === value;
    }
    return v === value;
  });
  
  // 如果是对象且有name字段，返回name；否则返回undefined
  if (typeof valueItem === 'object' && valueItem !== null && valueItem.name) {
    return valueItem.name;
  }
  
  return undefined;
};

// 根据维度信息和图例顺序重排chartData的函数
// chartData: 图表数据数组，每个元素包含name和data属性
// finalLegendShowNameKeyArr: 图例显示名称的维度键名数组，决定排序的维度顺序
// dimensionInfo: 维度信息数组，包含每个维度的data_key和value值列表
// 返回值: 按照维度顺序重排后的图表数据数组
const reorderChartDataByDimensions = (
  chartData: TChartData, // 待排序的图表数据数组
  finalLegendShowNameKeyArr: string[], // 图例维度键名数组，如['A', 'B', 'C']
  dimensionInfo: DimensionItem[], // 维度信息数组，包含每个维度的配置和值列表
): TChartData => {
  // 参数校验：如果chartData、finalLegendShowNameKeyArr或dimensionInfo为空，直接返回原始数据
  if (!chartData?.length || !finalLegendShowNameKeyArr?.length || !dimensionInfo?.length) {
    return chartData; // 返回原始数据，不进行排序
  }

  // 步骤1: 为每个维度构建「显示名/原值 -> 顺序索引」的映射表
  // 外层Map的key是维度键名（如'A'），value是内层Map
  // 内层Map的key是维度值的显示名或原值，value是该值在dimensionInfo中的索引位置
  const dimOrderMap = new Map<string, Map<string, number>>();
  // 遍历每个需要排序的维度键名
  finalLegendShowNameKeyArr.forEach((dimKey) => {
    // 从dimensionInfo中查找当前维度键名对应的维度信息
    const dim = dimensionInfo?.find(d => d.data_key === dimKey);
    // 如果找不到维度信息或维度值不是数组，跳过当前维度
    if (!dim || !Array.isArray(dim.value)) {
      return; // 跳过当前维度的处理
    }
    // 为当前维度创建一个映射表，用于存储「显示名/原值 -> 索引」的对应关系
    const m = new Map<string, number>();
    // 遍历当前维度的所有值，i为索引位置
    (dim?.value || []).forEach((v, i) => {
      // 获取维度值的原始值：如果是字符串则直接使用，如果是对象则取value属性
      const raw = !v ? '' : (typeof v === 'string' ? v : v.value);
      // 获取维度值的显示名：如果v是对象且有name属性，使用name；否则通过getDissionLable获取显示名
      const disp = !v ? '' : (typeof v === 'object' && v.name ? getDissionLable(dimKey, raw, v.name) : getDissionLable(dimKey, raw));
      // 将显示名映射到索引位置i
      m.set(disp, i);
      // 如果显示名和原值不同，也将原值映射到相同的索引位置（兜底处理，确保原值也能命中）
      if (disp !== raw) m.set(raw, i); // 兜底：原值也能命中
    });
    // 将当前维度的映射表存储到外层Map中，key为维度键名
    dimOrderMap.set(dimKey, m);
  });

  // 步骤2: 解析series的name字符串，将其转换为顺序索引元组
  // 例如：name为'A1_B1_C1'，解析后返回[0, 0, 0]（假设A1、B1、C1都在各自维度的第0位）
  const parseOrderTuple = (name: string): number[] => {
    // 将name字符串按'_'分割成数组，例如'A1_B1_C1' -> ['A1', 'B1', 'C1']
    const parts = String(name).split('_');
    // 遍历finalLegendShowNameKeyArr，为每个维度获取对应的索引
    return finalLegendShowNameKeyArr.map((dimKey, i) => {
      // 获取当前维度的映射表
      const orderMap = dimOrderMap.get(dimKey);
      // 获取name中对应位置的部分（如果不存在则为空字符串）
      const part = parts[i] ?? '';
      // 从映射表中查找该部分对应的索引位置
      const idx = orderMap?.get(part);
      // 如果找到索引则返回索引，否则返回正无穷（表示未命中，排序时放在最后）
      return idx ?? Number.POSITIVE_INFINITY; // 未命中放最后
    });
  };

  // 步骤3: 字典序比较函数，用于比较两个索引元组
  // 按照从左到右的顺序比较每个位置的索引值，返回比较结果
  const cmpTuple = (a: number[], b: number[]) => {
    // 遍历两个元组，比较次数取两者长度的最大值
    for (let i = 0;i < Math.max(a.length, b.length);i += 1) {
      // 获取a元组第i位的值，如果不存在则使用正无穷
      const da = a[i] ?? Number.POSITIVE_INFINITY;
      // 获取b元组第i位的值，如果不存在则使用正无穷
      const db = b[i] ?? Number.POSITIVE_INFINITY;
      // 如果两个值不相等，返回差值（负数表示a在前，正数表示b在前）
      if (da !== db) return da - db;
    }
    // 如果所有位置的值都相等，返回0（表示两个元组相等）
    return 0;
  };

  // 步骤4: 重排series数据，按照维度顺序进行排序
  // 创建chartData的浅拷贝，然后使用sort方法排序
  return [...chartData].sort((sa, sb) => cmpTuple(parseOrderTuple(sa.name), parseOrderTuple(sb.name)));
};